export interface InvoiceTemplate {
  id: string;
  name: string;
  description: string;
  preview: string;
  css: string;
}

export interface InvoiceData {
  invoiceNumber: string;
  orderNumber: string;
  date: string;
  dueDate: string;
  company: {
    name: string;
    address: string;
    phone: string;
    email: string;
    website: string;
    taxId: string;
    logo?: string;
    bankAccount?: {
      bankName: string;
      accountNumber: string;
      iban: string;
      swift: string;
    };
  };
  customer: {
    name: string;
    company: string;
    email: string;
    address: string;
  };
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  notes: string;
  status: string;
  paymentTerms: string;
}

export class SimpleInvoiceService {
  private static templates: InvoiceTemplate[] = [
    {
      id: 'modern',
      name: 'Modern',
      description: 'Clean, modern design with subtle colors',
      preview: 'Modern template preview',
      css: `
        .invoice-container {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          background: #ffffff;
          color: #333;
        }
        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid #e0e0e0;
        }
        .company-info h1 {
          color: #2c3e50;
          margin: 0;
          font-size: 28px;
          font-weight: 300;
        }
        .company-details {
          color: #7f8c8d;
          font-size: 14px;
          line-height: 1.4;
        }
        .invoice-details {
          text-align: right;
          color: #34495e;
        }
        .invoice-details h2 {
          margin: 0 0 10px 0;
          color: #2c3e50;
          font-size: 24px;
        }
        .invoice-meta {
          font-size: 14px;
          line-height: 1.6;
        }
        .customer-section {
          margin: 30px 0;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
        }
        .customer-section h3 {
          margin: 0 0 15px 0;
          color: #2c3e50;
          font-size: 18px;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin: 30px 0;
        }
        .items-table th {
          background: #34495e;
          color: white;
          padding: 15px;
          text-align: left;
          font-weight: 500;
        }
        .items-table td {
          padding: 15px;
          border-bottom: 1px solid #e0e0e0;
        }
        .items-table tr:nth-child(even) {
          background: #f8f9fa;
        }
        .totals-section {
          margin-top: 30px;
          text-align: right;
        }
        .totals-table {
          display: inline-block;
          min-width: 300px;
        }
        .totals-table td {
          padding: 8px 15px;
          border-bottom: 1px solid #e0e0e0;
        }
        .totals-table .total-row {
          font-weight: bold;
          font-size: 18px;
          background: #34495e;
          color: white;
        }
        .notes-section {
          margin-top: 30px;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
        }
        .notes-section h3 {
          margin: 0 0 15px 0;
          color: #2c3e50;
        }
        .footer {
          margin-top: 40px;
          text-align: center;
          color: #7f8c8d;
          font-size: 12px;
          border-top: 1px solid #e0e0e0;
          padding-top: 20px;
        }
      `
    },
    {
      id: 'classic',
      name: 'Classic',
      description: 'Traditional business invoice design',
      preview: 'Classic template preview',
      css: `
        .invoice-container {
          font-family: 'Times New Roman', serif;
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          background: #ffffff;
          color: #000;
        }
        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 3px solid #000;
        }
        .company-info h1 {
          color: #000;
          margin: 0;
          font-size: 32px;
          font-weight: bold;
          text-transform: uppercase;
          letter-spacing: 2px;
        }
        .company-details {
          color: #333;
          font-size: 14px;
          line-height: 1.5;
          margin-top: 10px;
        }
        .invoice-details {
          text-align: right;
          color: #000;
        }
        .invoice-details h2 {
          margin: 0 0 15px 0;
          color: #000;
          font-size: 28px;
          font-weight: bold;
        }
        .invoice-meta {
          font-size: 14px;
          line-height: 1.8;
        }
        .customer-section {
          margin: 30px 0;
          padding: 20px;
          border: 2px solid #000;
        }
        .customer-section h3 {
          margin: 0 0 15px 0;
          color: #000;
          font-size: 20px;
          font-weight: bold;
          text-transform: uppercase;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin: 30px 0;
          border: 2px solid #000;
        }
        .items-table th {
          background: #000;
          color: white;
          padding: 15px;
          text-align: left;
          font-weight: bold;
          text-transform: uppercase;
        }
        .items-table td {
          padding: 15px;
          border-bottom: 1px solid #000;
          border-right: 1px solid #000;
        }
        .items-table td:last-child {
          border-right: none;
        }
        .totals-section {
          margin-top: 30px;
          text-align: right;
        }
        .totals-table {
          display: inline-block;
          min-width: 300px;
          border: 2px solid #000;
        }
        .totals-table td {
          padding: 10px 15px;
          border-bottom: 1px solid #000;
        }
        .totals-table .total-row {
          font-weight: bold;
          font-size: 20px;
          background: #000;
          color: white;
        }
        .notes-section {
          margin-top: 30px;
          padding: 20px;
          border: 2px solid #000;
        }
        .notes-section h3 {
          margin: 0 0 15px 0;
          color: #000;
          font-size: 18px;
          font-weight: bold;
          text-transform: uppercase;
        }
        .footer {
          margin-top: 40px;
          text-align: center;
          color: #666;
          font-size: 12px;
          border-top: 2px solid #000;
          padding-top: 20px;
        }
      `
    },
    {
      id: 'minimal',
      name: 'Minimal',
      description: 'Simple, clean design with minimal styling',
      preview: 'Minimal template preview',
      css: `
        .invoice-container {
          font-family: Arial, sans-serif;
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          background: #ffffff;
          color: #333;
        }
        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 40px;
          padding-bottom: 20px;
          border-bottom: 1px solid #ddd;
        }
        .company-info h1 {
          color: #333;
          margin: 0;
          font-size: 24px;
          font-weight: normal;
        }
        .company-details {
          color: #666;
          font-size: 14px;
          line-height: 1.4;
          margin-top: 5px;
        }
        .invoice-details {
          text-align: right;
          color: #333;
        }
        .invoice-details h2 {
          margin: 0 0 10px 0;
          color: #333;
          font-size: 20px;
          font-weight: normal;
        }
        .invoice-meta {
          font-size: 14px;
          line-height: 1.6;
        }
        .customer-section {
          margin: 30px 0;
        }
        .customer-section h3 {
          margin: 0 0 15px 0;
          color: #333;
          font-size: 16px;
          font-weight: normal;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin: 30px 0;
        }
        .items-table th {
          background: #f5f5f5;
          color: #333;
          padding: 12px;
          text-align: left;
          font-weight: normal;
          border-bottom: 1px solid #ddd;
        }
        .items-table td {
          padding: 12px;
          border-bottom: 1px solid #eee;
        }
        .totals-section {
          margin-top: 30px;
          text-align: right;
        }
        .totals-table {
          display: inline-block;
          min-width: 250px;
        }
        .totals-table td {
          padding: 8px 12px;
          border-bottom: 1px solid #eee;
        }
        .totals-table .total-row {
          font-weight: bold;
          font-size: 16px;
          border-top: 2px solid #333;
        }
        .notes-section {
          margin-top: 30px;
        }
        .notes-section h3 {
          margin: 0 0 15px 0;
          color: #333;
          font-size: 16px;
          font-weight: normal;
        }
        .footer {
          margin-top: 40px;
          text-align: center;
          color: #999;
          font-size: 12px;
          border-top: 1px solid #eee;
          padding-top: 20px;
        }
      `
    }
  ];

  static getTemplates(): InvoiceTemplate[] {
    return this.templates;
  }

  static getTemplate(id: string): InvoiceTemplate | undefined {
    return this.templates.find(template => template.id === id);
  }

  static generateInvoiceHTML(data: InvoiceData, templateId: string = 'modern'): string {
    const template = this.getTemplate(templateId) || this.templates[0];
    const money = (n: unknown) => {
      const v = Number(n);
      return Number.isFinite(v) ? v : 0;
    };
    const items = Array.isArray(data.items) ? data.items : [];
    const taxRate = money(data.taxRate);
    const subtotal = money(data.subtotal);
    const taxAmount = money(data.taxAmount);
    const total = money(data.total);
    const escapeHtml = (value: unknown) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Invoice ${escapeHtml(data.invoiceNumber)}</title>
        <style>
          ${template.css}
          @media print {
            .invoice-container {
              margin: 0;
              padding: 0;
              box-shadow: none;
            }
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="invoice-header">
            <div class="company-info">
              <h1>${escapeHtml(data.company?.name)}</h1>
              <div class="company-details">
                ${escapeHtml(data.company?.address)}<br>
                Phone: ${escapeHtml(data.company?.phone)}<br>
                Email: ${escapeHtml(data.company?.email)}<br>
                ${data.company?.website ? `Website: ${escapeHtml(data.company.website)}<br>` : ''}
                ${data.company?.taxId ? `Tax ID: ${escapeHtml(data.company.taxId)}<br>` : ''}
                ${data.company?.bankAccount ? `
                <br><strong>Bank Details:</strong><br>
                Bank: ${escapeHtml(data.company.bankAccount.bankName)}<br>
                Account: ${escapeHtml(data.company.bankAccount.accountNumber)}<br>
                IBAN: ${escapeHtml(data.company.bankAccount.iban)}<br>
                SWIFT: ${escapeHtml(data.company.bankAccount.swift)}
                ` : ''}
              </div>
            </div>
            <div class="invoice-details">
              <h2>INVOICE</h2>
              <div class="invoice-meta">
                Invoice #: ${escapeHtml(data.invoiceNumber)}<br>
                Order #: ${escapeHtml(data.orderNumber)}<br>
                Date: ${escapeHtml(data.date)}<br>
                Due Date: ${escapeHtml(data.dueDate)}<br>
                Status: ${escapeHtml(data.status)}
              </div>
            </div>
          </div>

          <div class="customer-section">
            <h3>Bill To:</h3>
            <div>
              <strong>${escapeHtml(data.customer?.name)}</strong><br>
              ${data.customer?.company ? `${escapeHtml(data.customer.company)}<br>` : ''}
              ${escapeHtml(data.customer?.email)}<br>
              ${escapeHtml(data.customer?.address)}
            </div>
          </div>

          <table class="items-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(item => `
                <tr>
                  <td>${escapeHtml(item.description)}</td>
                  <td>${escapeHtml(item.quantity)}</td>
                  <td>€${money(item.unitPrice).toFixed(2)}</td>
                  <td>€${money(item.total).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="totals-section">
            <table class="totals-table">
              <tr>
                <td>Subtotal:</td>
                <td>€${subtotal.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Tax (${(taxRate * 100).toFixed(1)}%):</td>
                <td>€${taxAmount.toFixed(2)}</td>
              </tr>
              <tr class="total-row">
                <td>Total:</td>
                <td>€${total.toFixed(2)}</td>
              </tr>
            </table>
          </div>

          ${data.notes ? `
            <div class="notes-section">
              <h3>Notes</h3>
              <p>${escapeHtml(data.notes)}</p>
            </div>
          ` : ''}

          <div class="footer">
            <p>Payment Terms: ${escapeHtml(data.paymentTerms)}</p>
            <p>Thank you for your business!</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /** Open invoice HTML in a new tab (most reliable “download/view”). */
  static openInvoice(data: InvoiceData, templateId: string = 'modern'): boolean {
    const html = this.generateInvoiceHTML(data, templateId);
    const printWindow = window.open('', '_blank');
    if (!printWindow) return false;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    return true;
  }

  static downloadInvoice(data: InvoiceData, templateId: string = 'modern', filename?: string): void {
    const html = this.generateInvoiceHTML(data, templateId);
    // Prefer a visible tab — blob file downloads are often blocked or silent.
    const opened = this.openInvoice(data, templateId);

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `invoice-${data.invoiceNumber}.html`;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoking immediately can cancel the download in Chromium — delay it.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);

    if (!opened) {
      // Popup blocked; file download may still have started. Surface a hint via throw
      // only when neither path is usable — download click was at least attempted.
      console.warn('Invoice popup was blocked; file download was attempted instead.');
    }
  }

  static printInvoice(data: InvoiceData, templateId: string = 'modern'): void {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const html = this.generateInvoiceHTML(data, templateId);
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  }

  static copyInvoiceToClipboard(data: InvoiceData, templateId: string = 'modern'): Promise<void> {
    const html = this.generateInvoiceHTML(data, templateId);
    return navigator.clipboard.writeText(html);
  }
}
